import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AppSelectDropdown } from '../../AppSelectDropdown';
import { DatePicker } from '../../DatePicker';
import type {
  PayrollAdjustmentCategory,
  PayrollAdjustmentType,
} from '../../../utils/payrollAdjustmentCategories';
import {
  defaultBookingDateInPeriod,
  formatDayOfMonthLabel,
  yearMonthLabel,
} from '../../../utils/payrollAdjustmentCategories';

export type PayrollAdjustmentBookingForm = {
  type: PayrollAdjustmentType;
  employee_id: string;
  category_key: string;
  amount: string;
  note: string;
  recurring: boolean;
  booking_date: string;
  day_of_month: number;
  start_year_month: string;
  end_mode: 'exit' | 'date';
  end_year_month: string;
};

type EmployeeOption = { id: string; name: string };

type PayrollAdjustmentBookingDrawerProps = {
  open: boolean;
  title: string;
  employees: EmployeeOption[];
  categories: PayrollAdjustmentCategory[];
  /** Kategorie der bearbeiteten Buchung/Regel – bleibt wählbar, auch wenn ausgeblendet. */
  editingCategoryKey?: string | null;
  form: PayrollAdjustmentBookingForm;
  saving: boolean;
  hideEmployeeField?: boolean;
  onClose: () => void;
  onSave: () => void;
  onChange: (patch: Partial<PayrollAdjustmentBookingForm>) => void;
};

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => {
  const day = i + 1;
  return { value: String(day), label: `${day}. des Monats` };
});

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <p className="mb-2 text-xs font-medium text-app-text-muted">
      {children}
      {required ? <RequiredMark /> : null}
    </p>
  );
}

function parseFormAmount(value: string): number | null {
  const normalized = value.trim().replace(/\./g, '').replace(',', '.');
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isValidYmd(value: string): boolean {
  const s = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.getUTCFullYear() === y && utc.getUTCMonth() === m - 1 && utc.getUTCDate() === d;
}

function isValidYearMonth(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value.trim());
}

export function isPayrollAdjustmentFormValid(form: PayrollAdjustmentBookingForm): boolean {
  if (!form.employee_id.trim()) return false;
  if (!form.category_key.trim()) return false;
  if (parseFormAmount(form.amount) == null) return false;
  if (!form.recurring) {
    if (!isValidYmd(form.booking_date)) return false;
  } else {
    if (!Number.isInteger(form.day_of_month) || form.day_of_month < 1 || form.day_of_month > 31) {
      return false;
    }
    if (!isValidYearMonth(form.start_year_month)) return false;
    if (form.end_mode === 'date' && !isValidYearMonth(form.end_year_month)) return false;
  }
  return true;
}

function MonthInput({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-app-text-muted">
        {label}
        {required ? <RequiredMark /> : null}
      </span>
      <input
        type="month"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-app-border px-3 py-2 text-sm text-app-text"
      />
    </label>
  );
}

export function PayrollAdjustmentBookingDrawer({
  open,
  title,
  employees,
  categories,
  editingCategoryKey = null,
  form,
  saving,
  hideEmployeeField = false,
  onClose,
  onSave,
  onChange,
}: PayrollAdjustmentBookingDrawerProps) {
  const filteredCategories = React.useMemo(
    () =>
      categories.filter(
        (c) => c.type === form.type && (!c.hidden || c.key === editingCategoryKey),
      ),
    [categories, form.type, editingCategoryKey],
  );

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  React.useEffect(() => {
    if (!filteredCategories.some((c) => c.key === form.category_key) && filteredCategories[0]) {
      onChange({ category_key: filteredCategories[0].key });
    }
  }, [filteredCategories, form.category_key, onChange]);

  const canSave = React.useMemo(() => isPayrollAdjustmentFormValid(form), [form]);

  if (!open) return null;

  const categoryOptions = filteredCategories.map((c) => {
    const base = c.lohnart ? `${c.label} (${c.lohnart})` : c.label;
    return { value: c.key, label: c.hidden ? `${base} – ausgeblendet` : base };
  });

  const employeeOptions = employees.map((e) => ({ value: e.id, label: e.name }));

  const segBtn = (active: boolean) =>
    `flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      active ? 'bg-app-surface text-app-text' : 'text-app-text-muted hover:text-app-text'
    }`;

  return createPortal(
    <div className="fixed inset-0 z-[300]">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/35"
        aria-label="Schließen"
        onClick={onClose}
      />
      <div className="app-scrollbar absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col overflow-y-auto overscroll-contain border-l border-app-border bg-app-surface">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-app-border bg-app-surface px-5 py-4">
          <h2 className="text-base font-semibold text-app-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-5 p-5">
          <div>
            <FieldLabel required>Art der Buchung</FieldLabel>
            <div className="flex rounded-lg bg-app-surface-muted p-1">
              <button type="button" className={segBtn(form.type === 'bezug')} onClick={() => onChange({ type: 'bezug' })}>
                Bezug (+)
              </button>
              <button type="button" className={segBtn(form.type === 'abzug')} onClick={() => onChange({ type: 'abzug' })}>
                Abzug (−)
              </button>
            </div>
          </div>

          {!hideEmployeeField ? (
            <div>
              <FieldLabel required>Mitarbeiter</FieldLabel>
              <AppSelectDropdown
                value={form.employee_id}
                onChange={(employee_id) => onChange({ employee_id })}
                options={employeeOptions}
                searchable
                searchPlaceholder="Mitarbeiter suchen…"
              />
            </div>
          ) : null}

          <div>
            <FieldLabel required>Kategorie (Lohnart)</FieldLabel>
            <AppSelectDropdown
              value={form.category_key}
              onChange={(category_key) => onChange({ category_key })}
              options={categoryOptions}
              searchable
              searchPlaceholder="Kategorie suchen…"
            />
          </div>

          {!form.recurring ? (
            <div>
              <FieldLabel required>Buchungsdatum</FieldLabel>
              <DatePicker
                value={form.booking_date}
                onChange={(booking_date) => onChange({ booking_date })}
                className="w-full"
              />
            </div>
          ) : null}

          <label className="block">
            <span className="text-xs font-medium text-app-text-muted">
              Betrag (€)
              <RequiredMark />
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={form.amount}
              onChange={(e) => onChange({ amount: e.target.value })}
              placeholder="0,00"
              className="mt-1 w-full rounded-md border border-app-border px-3 py-2 text-sm text-app-text"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-app-text-muted">Notiz (optional)</span>
            <textarea
              value={form.note}
              onChange={(e) => onChange({ note: e.target.value })}
              rows={3}
              className="mt-1 w-full resize-none rounded-md border border-app-border px-3 py-2 text-sm text-app-text"
            />
          </label>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-app-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-app-text">Regelmäßig wiederholen</p>
              <p className="text-xs text-app-text-subtle">Jeden Monat am gleichen Kalendertag</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.recurring}
              onClick={() => {
                const recurring = !form.recurring;
                const patch: Partial<PayrollAdjustmentBookingForm> = { recurring };
                if (recurring && form.booking_date.length >= 10) {
                  patch.day_of_month = Number(form.booking_date.slice(8, 10)) || 15;
                }
                onChange(patch);
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors ${
                form.recurring ? 'border-emerald-500 bg-emerald-500' : 'border-app-border-strong bg-app-surface-muted'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-app-surface transition-transform mt-px ${
                  form.recurring ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {form.recurring ? (
            <div className="space-y-4 rounded-lg border border-app-border bg-app-surface-muted/80 p-4">
              <div>
                <FieldLabel required>Tag im Monat</FieldLabel>
                <AppSelectDropdown
                  value={String(form.day_of_month)}
                  onChange={(v) => onChange({ day_of_month: Number(v) || 15 })}
                  options={DAY_OF_MONTH_OPTIONS}
                  placeholder="Tag wählen"
                />
                <p className="mt-1.5 text-xs text-app-text-subtle">
                  Buchung erfolgt jeden Monat am {formatDayOfMonthLabel(form.day_of_month)} (ggf. letzter Tag bei kurzen Monaten).
                </p>
              </div>
              <MonthInput
                label="Beginnt ab Monat"
                value={form.start_year_month}
                onChange={(start_year_month) => onChange({ start_year_month })}
                required
              />
              <div>
                <FieldLabel required>Endet</FieldLabel>
                <div className="mb-3 flex rounded-lg bg-app-surface-muted p-1">
                  <button
                    type="button"
                    className={segBtn(form.end_mode === 'exit')}
                    onClick={() => onChange({ end_mode: 'exit' })}
                  >
                    Bis Austritt
                  </button>
                  <button
                    type="button"
                    className={segBtn(form.end_mode === 'date')}
                    onClick={() => onChange({ end_mode: 'date' })}
                  >
                    Bis Monat
                  </button>
                </div>
                {form.end_mode === 'date' ? (
                  <MonthInput
                    label="Endmonat"
                    value={form.end_year_month}
                    onChange={(end_year_month) => onChange({ end_year_month })}
                    required
                  />
                ) : (
                  <p className="text-xs text-app-text-subtle">Läuft bis zum Austrittsdatum des Mitarbeiters.</p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-app-border bg-app-surface px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-app-border px-4 py-2 text-sm font-medium text-app-text-muted hover:bg-app-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={saving || !canSave}
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <LoadingSpinner /> : null}
            Speichern
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function blankPayrollAdjustmentForm(
  fromYmd: string,
  employeeId: string,
  recurring = false,
): PayrollAdjustmentBookingForm {
  const yearMonth = fromYmd.slice(0, 7);
  return {
    type: 'abzug',
    employee_id: employeeId,
    category_key: 'strafzettel',
    amount: '',
    note: '',
    recurring,
    booking_date: defaultBookingDateInPeriod(fromYmd, 15),
    day_of_month: 15,
    start_year_month: yearMonth,
    end_mode: 'exit',
    end_year_month: yearMonth,
  };
}

export { yearMonthLabel };
